import { Injectable } from '@angular/core';
import { addressStringShort } from '@core/util/address';
import { Business } from '@models/businesses';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from './base-form-mapper';
import { NameFormattingSettings } from '@models/businessSettings/settings';

@Injectable({
	providedIn: 'root',
})
export class ON_GST_190_REBATE_SCHEDULE_Mapper extends BaseFormMapper<ON_GST_190_REBATE_SCHEDULE> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: ON_GST_190_REBATE_SCHEDULE,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as ON_GST_190_REBATE_SCHEDULE);

		if (
			!_formData?.propertyUnitStreet ||
			_formData?.propertyUnitStreet?.length === 0
		) {
			_formData.propertyUnitStreet = addressStringShort(
				matter?.propertyDetails?.address,
			);
		}

		if (!_formData?.propertyCity || _formData?.propertyCity?.length === 0) {
			_formData.propertyCity = matter?.propertyDetails?.address?.city;
		}

		if (
			!_formData?.propertyPostalCode ||
			_formData?.propertyPostalCode?.length === 0
		) {
			_formData.propertyPostalCode =
				matter?.propertyDetails?.address?.postcode?.replace(/ /g, '');
		}

		if (
			!_formData?.propertyTotalHSTPaidAmountOne ||
			_formData?.propertyTotalHSTPaidAmountOne?.length === 0
		) {
			_formData.propertyTotalHSTPaidAmountOne = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.hstProv +
					adjustments?.salePriceCalculated?.hstFed,
			);
		}

		if (
			(!_formData?.propertyTotalHSTPaidAmountTwo ||
				_formData?.propertyTotalHSTPaidAmountTwo?.length === 0) &&
			!isNaN(this.formValueToCents(_formData.propertyTotalHSTPaidAmountOne))
		) {
			_formData.propertyTotalHSTPaidAmountTwo = this.toFormDollarPrice(
				this.formValueToCents(_formData.propertyTotalHSTPaidAmountOne) * 8,
			);
		}

		if (
			(!_formData?.propertyTotalHSTPaidCalculation ||
				formData?.propertyTotalHSTPaidCalculation?.length === 0) &&
			!isNaN(this.formValueToCents(_formData.propertyTotalHSTPaidAmountTwo))
		) {
			_formData.propertyTotalHSTPaidCalculation = this.toFormDollarPrice(
				this.formValueToCents(_formData.propertyTotalHSTPaidAmountTwo) / 13,
			);
		}

		if (
			!_formData?.propertyPurchasePrice ||
			formData?.propertyPurchasePrice?.length === 0
		) {
			_formData.propertyPurchasePrice = this.toFormDollarPrice(
				adjustments?.salePriceCalculated?.nsp,
			);
		}

		if (
			(!_formData?.propertyNewHousingRebate ||
				formData?.propertyNewHousingRebate?.length === 0) &&
			!isNaN(this.formValueToCents(_formData?.propertyTotalHSTPaidCalculation))
		) {
			let val =
				this.formValueToCents(_formData?.propertyTotalHSTPaidCalculation) *
				0.75;
			if (val > 2400000) {
				val = 2400000;
			}
			_formData.propertyNewHousingRebate = this.toFormDollarPrice(val);
		}

		return _formData;
	}

	MapValues(): Partial<ON_GST_190_REBATE_SCHEDULE> {
		return {};
	}
}

const Mappings: ON_GST_190_REBATE_SCHEDULE = {
	propertyUnitStreet:
		'form1[0].Page1[0].PartA[0].NewHouseAddress[0].MailingAddress[0]',
	propertyCity: 'form1[0].Page1[0].PartA[0].NewHouseAddress[0].City[0]',
	propertyPostalCode:
		'form1[0].Page1[0].PartA[0].NewHouseAddress[0].BusinessAddressPostalCode[0]',
	propertyTotalHSTPaidAmountOne:
		'form1[0].Page1[0].PartB[0].Section1[0].LineA[0].Amount1[0].Amount[0]',
	propertyTotalHSTPaidAmountTwo:
		'form1[0].Page1[0].PartB[0].Section1[0].LineA[0].Amount2[0].Amount[0]',
	propertyTotalHSTPaidCalculation:
		'form1[0].Page1[0].PartB[0].Section1[0].LineA[0].Amount3[0].Amount[0]',
	propertyPurchasePrice:
		'form1[0].Page1[0].PartB[0].Section1[0].LineB[0].Amount[0].Amount[0]',
	propertyNewHousingRebate:
		'form1[0].Page1[0].PartB[0].Section1[0].LineC[0].Amount[0].Amount[0]',
	rebateType1BOr5TotalPurchasePrice:
		'form1[0].Page2[0].PartB_Continued[0].Section2[0].LineD[0].Amount[0].Amount[0]',
	rebateType1BOr5FairMarketValue:
		'form1[0].Page2[0].PartB_Continued[0].Section2[0].LineE[0].Amount[0].Amount[0]',
	rebateType1BOr5NewHousingRebate:
		'form1[0].Page2[0].PartB_Continued[0].Section2[0].LineF[0].Amount[0].Amount[0]',
	rebateType3TotalPurchasePrice:
		'form1[0].Page2[0].PartB_Continued[0].Section3[0].LineG[0].Amount[0].Amount[0]',
	rebateType3NewHousingRebate:
		'form1[0].Page2[0].PartB_Continued[0].Section3[0].LineH[0].Amount[0].Amount[0]',
};

export interface ON_GST_190_REBATE_SCHEDULE {
	propertyUnitStreet: string;
	propertyCity: string;
	propertyPostalCode: string;
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	propertyTotalHSTPaidAmountOne: any;
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	propertyTotalHSTPaidAmountTwo: any;
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	propertyTotalHSTPaidCalculation: any;
	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	propertyPurchasePrice: any;
	propertyNewHousingRebate: string;
	rebateType1BOr5TotalPurchasePrice: string;
	rebateType1BOr5FairMarketValue: string;
	rebateType1BOr5NewHousingRebate: string;
	rebateType3TotalPurchasePrice: string;
	rebateType3NewHousingRebate: string;
}
